// FILE: src/admin/pages/support/owners/SupportOwnerOverview.jsx
import React, { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  enableSupportOwnerLateCancelBlock,
  pushSupportOwnerLateCancelPenalty,
} from "../../../../../api/adminSupportApi";

/* --------------------- small utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x);
}
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function fmtInt(x) {
  const n = num(x);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
function fmtMoney(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function dt(x) {
  const s = safeStr(x);
  if (!s) return "—";
  return s.replace("T", " ").replace("+05:30", "").replace("Z", "").slice(0, 19);
}
function pct(part, total) {
  const t = num(total);
  if (t <= 0) return 0;
  return Math.round((num(part) * 1000) / t) / 10; // 1 decimal
}
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* --------------------- UI atoms --------------------- */
function Badge({ tone = "slate", children }) {
  const TONE = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-rose-200 bg-rose-50 text-rose-900",
    blue: "border-sky-200 bg-sky-50 text-sky-900",
    violet: "border-violet-200 bg-violet-50 text-violet-900",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-[12px] font-semibold",
        TONE[tone] || TONE.slate
      )}
    >
      {children}
    </span>
  );
}

function MetricCard({ title, value, hint, tone = "slate", subValue, subHint }) {
  const TONE = {
    slate: { bg: "bg-white", ring: "border-slate-200", bar: "bg-slate-900/10", accent: "bg-slate-500" },
    green: { bg: "bg-emerald-50", ring: "border-emerald-200", bar: "bg-emerald-900/10", accent: "bg-emerald-500" },
    amber: { bg: "bg-amber-50", ring: "border-amber-200", bar: "bg-amber-900/10", accent: "bg-amber-500" },
    red: { bg: "bg-rose-50", ring: "border-rose-200", bar: "bg-rose-900/10", accent: "bg-rose-500" },
    blue: { bg: "bg-sky-50", ring: "border-sky-200", bar: "bg-sky-900/10", accent: "bg-sky-500" },
    violet: { bg: "bg-violet-50", ring: "border-violet-200", bar: "bg-violet-900/10", accent: "bg-violet-500" },
  };
  const t = TONE[tone] || TONE.slate;

  return (
    <div className={classNames("rounded-2xl border p-4", t.bg, t.ring)}>
      <div className="flex items-start gap-3">
        <div className={classNames("mt-1 h-9 w-1 rounded-full", t.accent)} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title}</div>
          <div className="mt-2 text-3xl font-extrabold tabular-nums text-slate-900">{value}</div>
          {hint ? <div className="mt-1 text-xs text-slate-600">{hint}</div> : null}
          {subValue != null ? (
            <div className="mt-3 rounded-xl border border-white/60 bg-white/60 px-3 py-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{subHint}</div>
              <div className="mt-1 text-lg font-extrabold tabular-nums text-slate-900">{subValue}</div>
            </div>
          ) : null}
          <div className={classNames("mt-3 h-1.5 w-full rounded-full", t.bar)} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "slate" }) {
  const TONE = {
    slate: "border-slate-200 bg-white",
    green: "border-emerald-200 bg-white",
    amber: "border-amber-200 bg-white",
    red: "border-rose-200 bg-white",
    blue: "border-sky-200 bg-white",
    violet: "border-violet-200 bg-white",
  };
  return (
    <div className={classNames("rounded-xl border px-3 py-2", TONE[tone] || TONE.slate)}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-extrabold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function Signal({ title, tone, children }) {
  const TONE = {
    good: "border-emerald-200 bg-emerald-50 text-emerald-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    bad: "border-rose-200 bg-rose-50 text-rose-900",
    info: "border-sky-200 bg-sky-50 text-sky-900",
  };
  return (
    <div className={classNames("rounded-2xl border p-4", TONE[tone] || TONE.info)}>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{title}</div>
      <div className="mt-2 text-sm font-semibold">{children}</div>
    </div>
  );
}

export default function SupportOwnerOverview() {
  const { owner, refreshOwner } = useOutletContext();

  const stats = useMemo(() => {
    const total = num(owner?.bookingCount);
    const completed = num(owner?.completedBookingCount);
    const unfulfilled = num(owner?.unfulfilledBookingCount);
    const cancelled = num(owner?.cancelledBookingCount);

    const completionRate = pct(completed, total);
    const cancelRate = pct(cancelled, total);
    const unfulfilledRate = pct(unfulfilled, total);

    const reliabilityTone = unfulfilledRate >= 10 ? "bad" : cancelRate >= 20 ? "warn" : "good";
    const reliabilityText =
      unfulfilledRate >= 10
        ? `High unfulfilled rate (${unfulfilledRate}%). Investigate supply gaps / acceptance flow.`
        : cancelRate >= 20
          ? `High cancellation rate (${cancelRate}%). Check owner behaviour & policies.`
          : `Healthy completion (${completionRate}%). Owner looks reliable.`;

    const pendingOffers = num(owner?.pendingOfferCount);
    const responsivenessTone = pendingOffers >= 25 ? "bad" : pendingOffers >= 10 ? "warn" : "good";
    const responsivenessText =
      pendingOffers >= 25
        ? `Large pending inbox (${fmtInt(pendingOffers)}). Owner may be ignoring offers.`
        : pendingOffers >= 10
          ? `Some pending offers (${fmtInt(pendingOffers)}). Follow up if aging.`
          : `Inbox looks healthy (${fmtInt(pendingOffers)} pending).`;

    const payoutStatus = safeStr(owner?.payoutAccount?.status || owner?.payoutAccountStatus || "");
    const payoutTone =
      payoutStatus.toUpperCase() === "VERIFIED"
        ? "good"
        : payoutStatus.toUpperCase() === "ACTION_REQUIRED"
          ? "bad"
          : payoutStatus
            ? "warn"
            : "info";

    const payoutText =
      payoutStatus.toUpperCase() === "VERIFIED"
        ? "Payout account verified. Payouts should run smoothly."
        : payoutStatus.toUpperCase() === "ACTION_REQUIRED"
          ? "Payout account needs correction. Resolve to avoid payout failures."
          : payoutStatus
            ? `Payout account in progress (${payoutStatus}). Monitor verification.`
            : "No payout account info available.";

    // ✅ payout numbers from backend (new fields)
    const pendingAmt = num(owner?.pendingPayoutTotalAmount);
    
    const paidOutAmt = num(owner?.paidOutTotalAmount);
    const totalEarn = num(owner?.totalEarningsAmount) || (pendingAmt + paidOutAmt);

    const pendingShare = pct(pendingAmt, totalEarn);
    const paidShare = pct(paidOutAmt, totalEarn);

    // simple finance signal
    const financeTone =
      pendingAmt >= 50000 && payoutStatus.toUpperCase() !== "VERIFIED"
        ? "bad"
        : pendingAmt >= 20000
          ? "warn"
          : "good";

    const financeText =
      pendingAmt >= 50000 && payoutStatus.toUpperCase() !== "VERIFIED"
        ? `High pending payout (₹ ${fmtMoney(pendingAmt)}) but account not verified. Prioritize payout verification.`
        : pendingAmt >= 20000
          ? `Meaningful pending payout (₹ ${fmtMoney(pendingAmt)}). Monitor payout runs / account readiness.`
          : `Payout exposure looks fine (₹ ${fmtMoney(pendingAmt)} pending).`;

    return {
      total,
      completed,
      unfulfilled,
      cancelled,
      completionRate,
      cancelRate,
      unfulfilledRate,
      reliabilityTone,
      reliabilityText,
      responsivenessTone,
      responsivenessText,
      payoutTone,
      payoutText,
      payoutStatus,

      pendingAmt,
      paidOutAmt,
      totalEarn,
      pendingShare,
      paidShare,
      financeTone,
      financeText,
    };
  }, [owner]);

  const ownerName = owner?.name || "Owner";
  const phoneRaw = safeStr(owner?.phoneNumber);
  const phone = phoneRaw ? phoneRaw : "—";
  const email = owner?.email || "—";
  const lateCancelPenalty = owner?.lateCancelPenalty || null;
  const lateCancelBlocked = !!lateCancelPenalty?.blocked;

  // ✅ your rule: if phone exists, account must have been OTP verified
  const hasPhone = !!phoneRaw;

  async function handlePushPenalty() {
    const amountInput = window.prompt("Late cancellation charges in INR", String(lateCancelPenalty?.outstandingAmount || 1000));
    if (amountInput == null) return;
    const amount = Number(amountInput);
    if (!Number.isFinite(amount) || amount <= 0) {
      window.alert("Enter a valid amount.");
      return;
    }
    const bookingIdInput = window.prompt("Optional booking id", "");
    const planKeyInput = window.prompt("Optional plan key", "");
    const note = window.prompt("Reason or note", "Accepted scheduled booking was not fulfilled.") || "";
    await pushSupportOwnerLateCancelPenalty(owner?.id, {
      bookingId: bookingIdInput ? Number(bookingIdInput) : null,
      planKey: planKeyInput || "",
      amount,
      note,
    });
    await refreshOwner?.();
  }

  async function handleEnableWithoutPayment() {
    const note = window.prompt("Optional note for admin enable", "Enabled by admin without wallet deduction.") || "";
    await enableSupportOwnerLateCancelBlock(owner?.id, { note });
    await refreshOwner?.();
  }

  return (
    <div className="min-w-0 space-y-4">
      {/* Overview Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-lg font-extrabold text-slate-900">{ownerName}</div>

              {hasPhone ? <Badge tone="green">PHONE VERIFIED</Badge> : <Badge tone="amber">NO PHONE</Badge>}

              {stats.payoutStatus ? (
                <Badge
                  tone={
                    stats.payoutStatus.toUpperCase() === "VERIFIED"
                      ? "green"
                      : stats.payoutStatus.toUpperCase() === "ACTION_REQUIRED"
                        ? "red"
                        : "amber"
                  }
                >
                  PAYOUT: {stats.payoutStatus.toUpperCase()}
                </Badge>
              ) : null}
            </div>

            <div className="mt-2 text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{phone}</span>
              <span className="mx-2 text-slate-300">•</span>
              <span className="truncate">{email}</span>
              {owner?.address ? (
                <>
                  <span className="mx-2 text-slate-300">•</span>
                  <span className="truncate">{owner.address}</span>
                </>
              ) : null}
            </div>

            {/* mini stats row (✅ include payout numbers) */}
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
              <MiniStat label="Vehicles" value={fmtInt(owner?.vehicleCount)} tone="blue" />
              <MiniStat label="Pending Offers" value={fmtInt(owner?.pendingOfferCount)} tone="amber" />
              <MiniStat label="Pending Payout Txns" value={fmtInt(owner?.pendingPayoutTxnCount)} tone="violet" />

              <MiniStat label="Paid Out" value={`₹ ${fmtMoney(stats.paidOutAmt)}`} tone="green" />
              <MiniStat label="Pending Payout" value={`₹ ${fmtMoney(stats.pendingAmt)}`} tone="amber" />
              <MiniStat label="Total Earnings" value={`₹ ${fmtMoney(stats.totalEarn)}`} tone="blue" />

              <div className="lg:col-span-6">
                <MiniStat
                  label="Last Activity"
                  value={owner?.lastActivityAt ? dt(owner.lastActivityAt) : "—"}
                  tone="slate"
                />
              </div>
            </div>
          </div>

          {/* Quick Context */}
          <div className="w-full md:w-[360px]">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Quick Context</div>
              <div className="mt-2 text-sm text-slate-700">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">Last Booking</span>
                  <span className="font-extrabold tabular-nums text-slate-900">{owner?.lastBookingId ?? "—"}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-slate-600">Last Booking Status</span>
                  <span className="font-semibold text-slate-900">{owner?.lastBookingStatus ?? "—"}</span>
                </div>

                <div className="mt-3 h-px bg-slate-200" />

                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-slate-600">Pending Payout Bookings</span>
                  <span className="font-extrabold tabular-nums text-slate-900">{fmtInt(owner?.pendingPayoutBookingCount)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-slate-600">Pending Payout Amount</span>
                  <span className="font-extrabold tabular-nums text-slate-900">₹ {fmtMoney(stats.pendingAmt)}</span>
                </div>

                {/* ✅ NEW */}
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-slate-600">Paid Out Amount</span>
                  <span className="font-extrabold tabular-nums text-slate-900">₹ {fmtMoney(stats.paidOutAmt)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-slate-600">Total Earnings</span>
                  <span className="font-extrabold tabular-nums text-slate-900">₹ {fmtMoney(stats.totalEarn)}</span>
                </div>

                {/* split hint */}
                {stats.totalEarn > 0 ? (
                  <div className="mt-2 rounded-xl border border-white/70 bg-white/70 px-3 py-2 text-xs text-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Paid share</span>
                      <span className="font-extrabold tabular-nums text-slate-900">{stats.paidShare}%</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-slate-600">Pending share</span>
                      <span className="font-extrabold tabular-nums text-slate-900">{stats.pendingShare}%</span>
                    </div>
                  </div>
                ) : null}
              </div>

              {owner?.payoutAccount?.issueNote ? (
                <div className="mt-3 rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-amber-900">
                  <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Issue note</div>
                  <div className="mt-1 text-[13px]">{owner.payoutAccount.issueNote}</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className={classNames("rounded-2xl border p-4", lateCancelBlocked ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white")}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Late Cancellation Fee</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {lateCancelBlocked
                ? lateCancelPenalty?.message || "Owner is blocked pending late cancellation fee deduction."
                : "No owner late cancellation block is active."}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge tone={lateCancelBlocked ? "red" : "green"}>{lateCancelBlocked ? "BLOCKED" : "CLEAR"}</Badge>
              <Badge tone="amber">Outstanding: Rs {fmtMoney(lateCancelPenalty?.outstandingAmount || 0)}</Badge>
              {lateCancelPenalty?.bookingId ? <Badge tone="slate">Booking #{lateCancelPenalty.bookingId}</Badge> : null}
              {lateCancelPenalty?.planKey ? <Badge tone="slate">{lateCancelPenalty.planKey}</Badge> : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void handlePushPenalty()} className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50">
              Push fee block
            </button>
            <button
              type="button"
              onClick={() => void handleEnableWithoutPayment()}
              disabled={!lateCancelBlocked}
              className={classNames(
                "inline-flex h-10 items-center rounded-xl px-4 text-sm font-semibold",
                lateCancelBlocked ? "border border-slate-900 bg-slate-900 text-white" : "border border-slate-200 bg-slate-100 text-slate-400"
              )}
            >
              Enable without deduction
            </button>
          </div>
        </div>
        {lateCancelPenalty?.note ? (
          <div className="mt-3 rounded-xl border border-white/70 bg-white/70 px-3 py-2 text-sm text-slate-700">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Latest note</div>
            <div className="mt-1">{lateCancelPenalty.note}</div>
          </div>
        ) : null}
      </div>

      {/* KPI cards (Bookings) */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Total Bookings" value={fmtInt(stats.total)} hint="All bookings linked to owner vehicles" tone="blue" />
        <MetricCard title="Completed" value={fmtInt(stats.completed)} hint={`Completion rate: ${stats.completionRate}%`} tone="green" />
        <MetricCard
          title="Unfulfilled"
          value={fmtInt(stats.unfulfilled)}
          hint={`Unfulfilled rate: ${stats.unfulfilledRate}%`}
          tone={stats.unfulfilledRate >= 10 ? "red" : "amber"}
        />
        <MetricCard
          title="Cancelled"
          value={fmtInt(stats.cancelled)}
          hint={`Cancellation rate: ${stats.cancelRate}%`}
          tone={stats.cancelRate >= 20 ? "red" : "amber"}
        />
      </div>

      {/* KPI cards (✅ Payouts) */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <MetricCard
          title="Paid Out"
          value={`₹ ${fmtMoney(stats.paidOutAmt)}`}
          hint="Sum of SUCCESS payout transactions"
          tone="green"
        />
        <MetricCard
          title="Pending Payout"
          value={`₹ ${fmtMoney(stats.pendingAmt)}`}
          hint="Eligible completed rides not yet paid out"
          tone="amber"
          subValue={fmtInt(owner?.pendingPayoutBookingCount)}
          subHint="Pending bookings"
        />
        <MetricCard
          title="Total Earnings"
          value={`₹ ${fmtMoney(stats.totalEarn)}`}
          hint={stats.totalEarn > 0 ? `Paid: ${stats.paidShare}% • Pending: ${stats.pendingShare}%` : "—"}
          tone="blue"
        />
      </div>

      {/* Signals */}
      <div className="grid gap-4 lg:grid-cols-4">
        <Signal title="Reliability" tone={stats.reliabilityTone}>{stats.reliabilityText}</Signal>
        <Signal title="Responsiveness" tone={stats.responsivenessTone}>{stats.responsivenessText}</Signal>
        <Signal title="Payout Readiness" tone={stats.payoutTone}>{stats.payoutText}</Signal>
        <Signal title="Payout Exposure" tone={stats.financeTone}>{stats.financeText}</Signal>
      </div>

      {/* Payout account (summary) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-extrabold text-slate-900">Payout Account</div>
          {owner?.payoutAccount?.status ? (
            <Badge
              tone={
                String(owner.payoutAccount.status).toUpperCase() === "VERIFIED"
                  ? "green"
                  : String(owner.payoutAccount.status).toUpperCase() === "ACTION_REQUIRED"
                    ? "red"
                    : "amber"
              }
            >
              {String(owner.payoutAccount.status).toUpperCase()}
            </Badge>
          ) : (
            <Badge tone="slate">—</Badge>
          )}
        </div>

        <div className="mt-2 text-sm text-slate-600">
          Status: <span className="font-semibold text-slate-900">{owner?.payoutAccount?.status ?? "—"}</span>
          <span className="mx-2 text-slate-300">•</span>
          Method: <span className="font-semibold text-slate-900">{owner?.payoutAccount?.method ?? "—"}</span>
          <span className="mx-2 text-slate-300">•</span>
          Provider: <span className="font-semibold text-slate-900">{owner?.payoutAccount?.provider ?? "—"}</span>
        </div>

        {owner?.payoutAccount?.providerLastSyncAt ? (
          <div className="mt-2 text-xs text-slate-500">Last sync: {dt(owner.payoutAccount.providerLastSyncAt)}</div>
        ) : null}

        {owner?.payoutAccount?.issueNote ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="font-semibold">Issue note</div>
            <div className="mt-1 text-[13px]">{owner.payoutAccount.issueNote}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
